import { SkyAccordion, SkyAccordionItem, SkyText } from "@skypointcloud/skypoint-ui";
import { useMemo } from "react";
import { isFieldVisible } from "@shared/connectors/fieldRules";
import type { ControlType } from "@/components/connections/types";

/**
 * A read-only preview of a definition's `field_definitions` (FR-30, canvas 13
 * panel 02).
 *
 * The blocks are jsonb pass-through: the connector form renders them and
 * nothing on the server interprets them, so they are typed `unknown` in
 * `shared/types` and narrowed HERE rather than modelled. The narrowing is
 * deliberately forgiving — a field that does not look the way this component
 * expects still appears, as its own JSON, because a preview that silently drops
 * what it does not recognise is how a definition ends up looking complete and
 * being wrong.
 *
 * It is a `<pre>` labelled as a preview, never a textarea: nothing here is
 * editable, and a control that looks editable and is not is a worse lie than a
 * blank panel.
 */

interface PreviewField {
  name: string;
  group: string;
  type: string;
  required: boolean;
  /** Required, but only when the editor shows the field (hidden by default or shown by a rule). */
  requiredWhenShown: boolean;
  raw: unknown;
}

const UNGROUPED = "Other fields";

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() !== "" ? value : fallback;
}

/** The seed's control types, in words a person reads. */
export const CONTROL_TYPE_WORDS: Record<ControlType, string> = {
  text: "text",
  textarea: "long text",
  number: "number",
  password: "secret",
  url: "web address",
  toggle: "on or off",
  checkbox: "checkbox",
  radio: "one choice",
  select: "list",
  date: "date",
  email: "email address",
  multiselect: "several choices",
  file: "file",
  datetime: "date and time",
  folderselector: "folder",
  keyvalue: "name and value pairs",
};

function controlWord(control: string): string | undefined {
  return Object.hasOwn(CONTROL_TYPE_WORDS, control)
    ? CONTROL_TYPE_WORDS[control as ControlType]
    : undefined;
}

/** True when the editor does not always show the field: hidden by default or moved by a rule. */
function isConditional(record: Record<string, unknown> | null): boolean {
  if (!record) return false;
  if (!isFieldVisible(record, {})) return true;
  const deps = record.dependencies;
  return (
    Array.isArray(deps) &&
    deps.some((d) => {
      const type = asRecord(asRecord(d)?.effect)?.type;
      return type === "show" || type === "hide";
    })
  );
}

/** group id to the seed's group name, in the seed's order. */
function groupNames(fieldGroups: unknown): Map<string, string> {
  const names = new Map<string, string>();
  if (!Array.isArray(fieldGroups)) return names;
  const rows = fieldGroups
    .map((g, i) => ({ g: asRecord(g), i }))
    .filter((row) => row.g && typeof row.g.id === "string");
  rows.sort((a, b) => Number(a.g?.order ?? a.i) - Number(b.g?.order ?? b.i));
  for (const { g } of rows) names.set(String(g?.id), text(g?.label ?? g?.name, String(g?.id)));
  return names;
}

export function toPreviewFields(fieldDefinitions: unknown, fieldGroups?: unknown): PreviewField[] {
  if (!Array.isArray(fieldDefinitions)) return [];
  const names = groupNames(fieldGroups);
  return fieldDefinitions.map((entry, index) => {
    const record = asRecord(entry);
    const groupId = typeof record?.groupId === "string" ? record.groupId : "";
    const control = typeof record?.controlType === "string" ? record.controlType : "";
    const isRequired =
      record?.required === true || asRecord(asRecord(record?.validation)?.required)?.value === true;
    const conditional = isRequired && isConditional(record);
    return {
      name: text(record?.name ?? record?.id, `field ${index + 1}`),
      group: names.get(groupId) ?? text(record?.groupName ?? record?.group, UNGROUPED),
      type: controlWord(control) ?? text(record?.controlType ?? record?.type, "not stated"),
      required: isRequired && !conditional,
      requiredWhenShown: conditional,
      raw: entry,
    };
  });
}

export function ConnectorFieldPreview({
  fieldDefinitions,
  fieldGroups,
  isLoading,
  isError,
}: {
  fieldDefinitions: unknown;
  fieldGroups?: unknown;
  isLoading: boolean;
  isError: boolean;
}) {
  const groups = useMemo(() => {
    const fields = toPreviewFields(fieldDefinitions, fieldGroups);
    const byGroup = new Map<string, PreviewField[]>();
    for (const field of fields) {
      const list = byGroup.get(field.group) ?? [];
      list.push(field);
      byGroup.set(field.group, list);
    }
    const order = [...groupNames(fieldGroups).values()];
    // "Other fields" is always last; a name the seed does not list sorts just before it.
    const rank = (name: string) => {
      if (name === UNGROUPED) return Number.MAX_SAFE_INTEGER;
      const at = order.indexOf(name);
      return at === -1 ? order.length : at;
    };
    return [...byGroup.entries()].sort((a, b) => rank(a[0]) - rank(b[0]));
  }, [fieldDefinitions, fieldGroups]);

  if (isLoading) {
    return (
      <SkyText size={200} color="muted" block>
        Reading the definition's fields…
      </SkyText>
    );
  }

  if (isError) {
    return (
      <SkyText size={200} color="muted" block>
        The field preview is not available. It is read from the connector catalog, which needs an
        instance context. Everything above comes from the definition itself.
      </SkyText>
    );
  }

  if (groups.length === 0) {
    return (
      <SkyText size={200} color="muted" block>
        This definition declares no fields.
      </SkyText>
    );
  }

  const firstGroup = groups[0]?.[0];

  return (
    <SkyAccordion
      multiple
      size="small"
      defaultOpenItems={firstGroup ? [firstGroup] : []}
      testId="connector-field-preview"
    >
      {groups.map(([group, fields]) => (
        <SkyAccordionItem
          key={group}
          value={group}
          heading={`${group} · ${fields.length} ${fields.length === 1 ? "field" : "fields"}`}
        >
          <div className="flex flex-col gap-3">
            <ul className="flex flex-col gap-1">
              {fields.map((field) => (
                <li key={field.name} className="flex items-baseline gap-2">
                  <SkyText size={200} font="monospace">
                    {field.name}
                  </SkyText>
                  <SkyText size={200} color="muted">
                    {field.type}
                    {field.required ? " · required" : ""}
                    {field.requiredWhenShown ? " · required when shown" : ""}
                  </SkyText>
                </li>
              ))}
            </ul>
            <div>
              <SkyText as="h4" size={300} weight="semibold" block id={`preview-${group}`}>
                Read-only preview of field_definitions
              </SkyText>
              <pre
                aria-labelledby={`preview-${group}`}
                tabIndex={0}
                className="max-h-64 overflow-auto rounded-md border border-border bg-muted p-3 text-xs"
              >
                {JSON.stringify(
                  fields.map((field) => field.raw),
                  null,
                  2,
                )}
              </pre>
            </div>
          </div>
        </SkyAccordionItem>
      ))}
    </SkyAccordion>
  );
}
